import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import ChatPresentation from 'chat-ui/ChatPresentation';
import { useThemeQuery } from 'chat-ui/src/reactQuery/get';
import useChatStore from 'chat-ui/src/store';
import { toast } from 'react-toastify';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { getErrorStatus } from '@common/utils';
import { EProjectStatus, EStatusCode } from '@/enums';
import { getTestChatHistory, testChatRegister, axiosInstanceChat } from './axios/axiosInstanceChat';
import { ChatProps } from '@/types';
import { flattenGroupToQuestions } from '@/utils';

const chatCannotRegisterError =
  'We cannot find this chat. Please check the link or contact the owner of the chat.';
const errorSendingMessage = "We can't send your message. Please try again.";
const incompleteChatSetupWarning =
  'Please ensure that both the opening message and the first question are filled out before initiating the test conversation.';
const incompleteEmbedSetupWarning =
  'Please ensure the first question is filled out before initiating the test conversation.';

function DashboardTestChat({ projectId, chatHeight, singleProjectData }: ChatProps) {
  const {
    setInitialMessages,
    setSeen,
    addErrorMessage,
    setRefreshChatHistory,
    currentConversationId,
    setCurrentConversationId,
  } = useChatStore((state) => state);

  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const { setChatSettingsData } = useChatSettingsStore();

  const { data: themeData } = useThemeQuery(projectId);

  const hasOpeningMessage = !!singleProjectData?.opening_message;

  // Use existing helper to flatten all questions from nested groups and check if any have content
  const hasFirstQuestion = singleProjectData?.interview_questions
    ? flattenGroupToQuestions(singleProjectData.interview_questions).some(
        (q) => q.question && q.question.trim() !== '',
      )
    : false;
  // TELV1-186 — embed projects do not require an opening message; only a first
  // question is needed to start a test conversation (mirrors the embed-aware
  // publish-readiness + backend @ValidateIf for embed).
  const isEmbedProject = singleProjectData?.project_type === 'embed';
  const isSetupComplete = hasFirstQuestion && (isEmbedProject || hasOpeningMessage);
  const typeOfInterview = singleProjectData?.type_of_interview;
  const isProjectCompleted = singleProjectData?.status === EProjectStatus.COMPLETED;
  const handleChatRegister = useCallback(
    async (onError?: () => void) => {
      try {
        const data = await testChatRegister(projectId);
        if (data?.conversationId && data?.type_of_interview) {
          setCurrentConversationId?.(data?.conversationId);
          setChatSettingsData(data?.type_of_interview as string[]);
        }
      } catch (error) {
        onError?.();
      }
    },
    [projectId, setCurrentConversationId, setChatSettingsData],
  );

  const getHistoryAndSetInitMsg = useCallback(async () => {
    if (!currentConversationId) return;
    try {
      const { messages } = await getTestChatHistory(currentConversationId);
      setInitialMessages(messages);
      setSeen();
    } catch (error) {
      // TELV1-307 — axiosInstanceChat unwraps `error.response`, so
      // reading `axiosError.response?.status` inline returned undefined
      // and the INVALID_CREDENTIALS guard never short-circuited the
      // error-message dispatch.
      const axiosError = error as AxiosError;
      if (axiosError && getErrorStatus(axiosError) !== EStatusCode.INVALID_CREDENTIALS) {
        addErrorMessage(errorSendingMessage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  useEffect(() => {
    setRefreshChatHistory(() => getHistoryAndSetInitMsg());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  const handleRestartChat = useCallback(async () => {
    setIsRefreshLoading(true);
    try {
      if (currentConversationId) {
        const response = await axiosInstanceChat.post(
          `${import.meta.env.VITE_CHAT_URL}/chat/reset/${projectId}/${currentConversationId}`,
          undefined,
        );

        // Re-register the chat to fetch the latest project data (including updated questions)
        await handleChatRegister();

        setIsRefreshLoading(false);
        setInitialMessages(response?.data?.messages);
      } else {
        setIsRefreshLoading(false);
        // Embed studies have no opening message — only the first question is
        // required, so the warning should not mention an opening message.
        toast.info(isEmbedProject ? incompleteEmbedSetupWarning : incompleteChatSetupWarning);
      }
    } catch (error) {
      setIsRefreshLoading(false);
      addErrorMessage(errorSendingMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentConversationId, handleChatRegister]);

  useEffect(() => {
    if (isSetupComplete && projectId) {
      handleChatRegister(() => addErrorMessage(chatCannotRegisterError));
    } else {
      setCurrentConversationId('');
      setInitialMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSetupComplete, projectId]);

  useEffect(() => {
    if (currentConversationId) {
      getHistoryAndSetInitMsg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  useEffect(() => {
    setChatSettingsData(typeOfInterview as string[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOfInterview, projectId]);

  return (
    <ChatPresentation
      chatHeight={chatHeight}
      handleRestartChat={handleRestartChat}
      restartChatButton
      isTestChat
      isRefreshLoading={isRefreshLoading}
      themeData={themeData}
      isProjectCompleted={isProjectCompleted}
    />
  );
}

export default DashboardTestChat;
